import { useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { Alert, Button, Input, Modal } from './ui';

/**
 * What one or more users may reach, changed for all of them at once
 * (services/grants.ts on the hub; PUT /admin/access and /admin/caps).
 *
 * Nothing is saved until Apply: changes are staged in the dialog first, so
 * several can be made and checked together. Each kind of resource has its own
 * levels (Magewell has no "full", recordings are given as a whole), taken
 * from the hub's KIND_INFO.
 */

export type Level = 'view' | 'use' | 'edit' | 'full';
export interface KindInfo { id: string; label: string; perItem: boolean; levels: Partial<Record<Level, string>> }
export interface Resource { id: string; name: string; detail?: string; ownerId?: string | null }
export interface AccessUser {
  id: string; email: string; name: string; active: boolean; isAdmin: boolean;
  caps: string[]; grants: { kind: string; id: string; level: Level }[];
}
export interface AccessMatrix {
  levels: Level[];
  caps: string[];
  kinds: KindInfo[];
  users: AccessUser[];
  resources: Record<string, Resource[]>;
}

const LEVEL_ORDER: Level[] = ['view', 'use', 'edit', 'full'];
const CAP_LABEL: Record<string, string> = { 'create:machine': 'Launch new machines', 'create:encoder': 'Make new encoders', 'create:flow': 'Make new flows' };
const key = (kind: string, id: string) => `${kind}:${id}`;

export function AccessModal({ matrix, users, onClose, onSaved }: {
  matrix: AccessMatrix;
  users: AccessUser[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const kinds = useMemo(() => new Map(matrix.kinds.map((k) => [k.id, k])), [matrix.kinds]);
  const label = (kind: string, id: string) =>
    id === '*' ? `All ${kinds.get(kind)?.label.toLowerCase() ?? kind}` : matrix.resources[kind]?.find((r) => r.id === id)?.name ?? id;

  // What each user has now, keyed kind:id -> level per user.
  const current = useMemo(() => {
    const m = new Map<string, Map<string, Level>>();
    for (const u of users) for (const g of u.grants) {
      const k = key(g.kind, g.id);
      if (!m.has(k)) m.set(k, new Map());
      m.get(k)!.set(u.id, g.level);
    }
    return m;
  }, [users]);

  /** Staged: kind:id -> new level for everyone, or null to remove from everyone. */
  const [staged, setStaged] = useState<Map<string, Level | null>>(new Map());
  const [caps, setCaps] = useState<Map<string, boolean | null>>(() => {
    const m = new Map<string, boolean | null>();
    for (const c of matrix.caps) {
      const n = users.filter((u) => u.caps.includes(c)).length;
      m.set(c, n === 0 ? false : n === users.length ? true : null);
    }
    return m;
  });
  const [capsTouched, setCapsTouched] = useState(false);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [addLevel, setAddLevel] = useState<Level>('view');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stage = (k: string, level: Level | null) => setStaged((s) => new Map(s).set(k, level));
  const unstage = (k: string) => setStaged((s) => { const n = new Map(s); n.delete(k); return n; });

  // Rows shown under "Has access": what anyone selected has, plus what is staged.
  const rows = [...new Set([...current.keys(), ...staged.keys()])]
    .map((k) => { const i = k.indexOf(':'); return { k, kind: k.slice(0, i), id: k.slice(i + 1) }; })
    .sort((a, b) => a.kind.localeCompare(b.kind) || (a.id === '*' ? -1 : b.id === '*' ? 1 : label(a.kind, a.id).localeCompare(label(b.kind, b.id))));

  // Resources matching the search, "All <kind>" first in each kind.
  const q = search.trim().toLowerCase();
  const candidates = matrix.kinds
    .filter((k) => !kindFilter || k.id === kindFilter)
    .flatMap((k) => [
      { kind: k.id, id: '*', name: `All ${k.label.toLowerCase()}`, detail: k.perItem ? 'every one, including new ones' : 'given as a whole' },
      ...(k.perItem ? (matrix.resources[k.id] ?? []).map((r) => ({ kind: k.id, id: r.id, name: r.name, detail: r.detail ?? '' })) : []),
    ])
    .filter((r) => !q || `${r.name} ${r.detail} ${kinds.get(r.kind)?.label}`.toLowerCase().includes(q));

  // Levels every picked kind has, so one choice fits them all.
  const pickedKinds = new Set([...picked].map((k) => k.slice(0, k.indexOf(':'))));
  const common = LEVEL_ORDER.filter((l) => [...pickedKinds].every((k) => kinds.get(k)?.levels[l]));
  const addOptions = pickedKinds.size ? common : LEVEL_ORDER;

  const changes = [...staged.entries()].map(([k, level]) => { const i = k.indexOf(':'); return { kind: k.slice(0, i), id: k.slice(i + 1), level }; });
  const capChange = capsTouched && [...caps.values()].every((v) => v !== null);
  const pending = changes.length + (capChange ? 1 : 0);

  async function apply(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      const userIds = users.map((u) => u.id);
      if (changes.length) await api.put('/admin/access', { userIds, changes });
      if (capChange) await api.put('/admin/caps', { userIds, caps: [...caps.entries()].filter(([, v]) => v).map(([c]) => c) });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the access.');
      setSaving(false);
    }
  }

  const names = users.length <= 3 ? users.map((u) => u.name).join(', ') : `${users.length} users`;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Access for ${names}`}
      width="max-w-3xl"
      footer={
        <>
          <span className="mr-auto text-xs text-ink-muted">{pending ? `${pending} change${pending === 1 ? '' : 's'} to apply to ${users.length === 1 ? users[0].name : `all ${users.length}`}` : 'Nothing changed yet'}</span>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!pending || saving} loading={saving} onClick={() => void apply()}>Apply</Button>
        </>
      }
    >
      <div className="space-y-5 text-sm">
        {error && <Alert tone="critical">{error}</Alert>}
        {users.length > 1 && (
          <p className="text-xs text-ink-muted">Every change here is made for all {users.length} of them. Where they differ now, the row says so.</p>
        )}

        {/* ---- What they have ---- */}
        <section>
          <h3 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-muted">Has access to</h3>
          {rows.length === 0 ? (
            <div className="rounded border border-dashed border-grid px-3 py-3 text-xs text-ink-muted">Nothing. They can sign in, and see nothing until given access below.</div>
          ) : (
            <div className="divide-y divide-grid rounded border border-grid">
              {rows.map(({ k, kind, id }) => {
                const info = kinds.get(kind);
                const now = current.get(k);
                const s = staged.get(k);
                const isStaged = staged.has(k);
                const levels = [...new Set(users.map((u) => now?.get(u.id) ?? null))];
                const mixed = levels.length > 1;
                const shown: Level | '' = isStaged ? (s ?? '') : (!mixed && levels[0]) ? levels[0] : '';
                return (
                  <div key={k} className={`flex flex-wrap items-center gap-2 px-3 py-1.5 ${isStaged ? 'bg-raised/40' : ''}`}>
                    <span className="w-28 shrink-0 text-[11px] text-ink-muted">{info?.label ?? kind}</span>
                    <span className={`min-w-0 flex-1 truncate ${isStaged && s === null ? 'text-ink-muted line-through' : ''} ${id === '*' ? 'font-medium' : ''}`}>
                      {label(kind, id)}
                    </span>
                    {mixed && !isStaged && (
                      <span className="text-[11px] text-ink-muted">
                        {users.map((u) => `${u.name.split(' ')[0]}: ${now?.get(u.id) ?? 'none'}`).join(' · ')}
                      </span>
                    )}
                    <select
                      className="rounded border border-grid bg-surface px-1.5 py-0.5 text-xs"
                      value={shown}
                      title={shown ? info?.levels[shown as Level] : undefined}
                      onChange={(e) => stage(k, (e.target.value || null) as Level | null)}
                    >
                      {mixed && !isStaged && <option value="">mixed…</option>}
                      {LEVEL_ORDER.filter((l) => info?.levels[l]).map((l) => (
                        <option key={l} value={l}>{l}: {info!.levels[l]}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="px-1 text-ink-muted hover:text-critical"
                      title={isStaged ? 'Undo' : 'Remove from everyone selected'}
                      onClick={() => (isStaged && !now ? unstage(k) : isStaged && s === null ? unstage(k) : stage(k, null))}
                    >
                      {isStaged && s === null ? '↺' : '✕'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ---- Add ---- */}
        <section>
          <h3 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-muted">Give access</h3>
          <div className="flex flex-wrap gap-2">
            <Input className="min-w-[12rem] flex-1" placeholder="Search machines, encoders, devices, channels…" value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
            <select className="rounded border border-grid bg-surface px-2 text-sm" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
              <option value="">Every kind</option>
              {matrix.kinds.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          </div>
          <div className="mt-2 max-h-64 overflow-y-auto rounded border border-grid">
            {candidates.length === 0 && <div className="px-3 py-3 text-xs text-ink-muted">Nothing matches.</div>}
            {candidates.map((r) => {
              const k = key(r.kind, r.id);
              return (
                <label key={k} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-raised/40">
                  <input
                    type="checkbox"
                    className="accent-series-1"
                    checked={picked.has(k)}
                    onChange={(e) => setPicked((p) => { const n = new Set(p); if (e.target.checked) n.add(k); else n.delete(k); return n; })}
                  />
                  <span className="w-28 shrink-0 text-[11px] text-ink-muted">{kinds.get(r.kind)?.label}</span>
                  <span className={`min-w-0 flex-1 truncate ${r.id === '*' ? 'font-medium' : ''}`}>{r.name}</span>
                  <span className="truncate text-[11px] text-ink-muted">{r.detail}</span>
                </label>
              );
            })}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-xs text-ink-muted">{picked.size} picked, at</span>
            <select
              className="rounded border border-grid bg-surface px-2 py-1 text-sm"
              value={addOptions.includes(addLevel) ? addLevel : addOptions[0] ?? 'view'}
              onChange={(e) => setAddLevel(e.target.value as Level)}
            >
              {addOptions.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <Button
              disabled={!picked.size || !addOptions.length}
              onClick={() => {
                const level = addOptions.includes(addLevel) ? addLevel : addOptions[0];
                for (const k of picked) stage(k, level);
                setPicked(new Set());
              }}
            >
              Add to the list above
            </Button>
            {pickedKinds.size > 0 && !addOptions.length && <span className="text-xs text-warning">Those kinds share no level: add them separately.</span>}
          </div>
          {pickedKinds.size === 1 && (
            <div className="mt-1 text-[11px] text-ink-muted">
              {LEVEL_ORDER.filter((l) => kinds.get([...pickedKinds][0])?.levels[l]).map((l) => `${l}: ${kinds.get([...pickedKinds][0])!.levels[l]}`).join(' · ')}
            </div>
          )}
        </section>

        {/* ---- Making new things ---- */}
        <section>
          <h3 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-muted">May make new</h3>
          <div className="flex flex-wrap gap-4">
            {matrix.caps.map((c) => {
              const v = caps.get(c);
              return (
                <label key={c} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="accent-series-1"
                    checked={v === true}
                    ref={(el) => { if (el) el.indeterminate = v === null; }}
                    onChange={(e) => { setCaps((m) => new Map(m).set(c, e.target.checked)); setCapsTouched(true); }}
                  />
                  {CAP_LABEL[c] ?? c}
                </label>
              );
            })}
          </div>
          <div className="mt-1 text-[11px] text-ink-muted">Whoever makes something has full access to it.</div>
        </section>
      </div>
    </Modal>
  );
}
